import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { contactAPI } from '../lib/api';
import { pickText, pickPlain } from '../lib/content';

export const ContactSection = ({ t, content, lang }) => {
  const { toast } = useToast();
  const c = content?.contact || {};
  const cTitle = pickText(c.title, lang, t.contact.title);
  const cSubtitle = pickText(c.subtitle, lang, t.contact.subtitle);
  const cInfoTitle = pickText(c.info_title, lang, t.contact.info.title);
  const cAddress = pickText(c.address, lang, t.contact.info.address);
  const cEmail = pickPlain(c.email, 'info@kaya-group.eu');
  const cPhoneKosovo = pickPlain(c.phone_kosovo, '+383 48 781 888');
  const cPhoneAlbania = pickPlain(c.phone_albania, '+355 67 200 0026');
  const cWhatsappNumber = pickPlain(c.whatsapp_number, '355672000026');
  const cWhatsappDisplay = pickPlain(c.whatsapp_display, '+355 67 200 0026');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await contactAPI.submit(formData);
      toast({
        title: lang === 'sq' ? 'Mesazhi u dërgua!' : 'Message sent!',
        description: lang === 'sq' 
          ? 'Faleminderit për kontaktin. Do t’ju përgjigjemi së shpejti.'
          : 'Thank you for contacting us. We will respond soon.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({
        title: lang === 'sq' ? 'Gabim' : 'Error',
        description: (err.response?.data?.detail || 'Something went wrong').toString(),
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${cWhatsappNumber}`, '_blank');
  };

  return (
    <section id="contact" className="py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20 space-y-6">
          <div className="inline-block px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider mb-4 animate-fadeInUp">
            {cTitle}
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {cSubtitle}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full animate-pulse" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-2 space-y-6 animate-fadeInUp" style={{ animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards' }}>
            <div className="relative">
              {/* Glowing effect */}
              <div className="absolute -inset-1 bg-gradient-to-br from-[#C5A05E] to-[#D4B06E] opacity-50 blur-2xl rounded-3xl" />
              
              <Card className="relative backdrop-blur-2xl bg-gradient-to-br from-[#C5A05E]/20 via-[#C5A05E]/10 to-transparent border border-[#C5A05E]/30 overflow-hidden">
                {/* Animated background gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C5A05E]/10 to-transparent animate-shimmer" style={{ animation: 'shimmer 3s infinite' }} />
                
                <CardContent className="p-8 space-y-8 relative z-10">
                  <div>
                    <h3 className="text-3xl font-bold mb-2 text-white">{cInfoTitle}</h3>
                    <p className="text-gray-300 text-sm">Kaya Property Management</p>
                  </div>

                  <div className="space-y-6">
                    {[
                      { icon: MapPin, label: lang === 'sq' ? 'Adresa' : 'Address', value: cAddress },
                      { icon: Mail, label: 'Email', value: cEmail },
                      { icon: Phone, label: 'Kosovë', value: cPhoneKosovo },
                      { icon: Phone, label: 'Shqipëri', value: cPhoneAlbania },
                      { icon: MessageCircle, label: 'WhatsApp', value: cWhatsappDisplay }
                    ].map((item, index) => (
                      <div key={index} className="group flex items-start gap-4 p-4 backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 hover:border-[#C5A05E]/50 hover:bg-white/10 transition-all duration-300 cursor-pointer">
                        <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-[#C5A05E]/20 group-hover:scale-110 transition-all">
                          <item.icon className="w-6 h-6 text-[#C5A05E]" />
                        </div>
                        <div>
                          <div className="font-semibold mb-1 text-white">{item.label}</div>
                          <div className="text-sm text-gray-300">{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* WhatsApp Button */}
                  <Button
                    onClick={handleWhatsApp}
                    className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#25D366] text-white font-bold shadow-lg shadow-[#25D366]/30 hover:shadow-[#25D366]/50 transition-all duration-300 hover:scale-105 group/btn py-6 rounded-2xl"
                    size="lg"
                  >
                    <MessageCircle className="mr-2 w-6 h-6 group-hover/btn:rotate-12 transition-transform" />
                    {t.contact.whatsapp}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-3 animate-fadeInUp" style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}>
            <Card className="backdrop-blur-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-all duration-500">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-3">
                      {t.contact.name}
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] focus:ring-[#C5A05E]/30 rounded-xl py-6"
                      placeholder={t.contact.name}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-3">
                        {t.contact.email}
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] focus:ring-[#C5A05E]/30 rounded-xl py-6"
                        placeholder={t.contact.email}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white mb-3">
                        {t.contact.phone}
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] focus:ring-[#C5A05E]/30 rounded-xl py-6"
                        placeholder={t.contact.phone}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white mb-3">
                      {t.contact.message}
                    </label>
                    <Textarea
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] focus:ring-[#C5A05E]/30 resize-none rounded-xl"
                      placeholder={t.contact.message}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="relative overflow-hidden w-full bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-bold shadow-2xl shadow-[#C5A05E]/50 transition-all duration-500 hover:scale-105 hover:shadow-[#C5A05E]/70 group py-7 rounded-2xl border-2 border-[#C5A05E]/50"
                    size="lg"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      {isSubmitting ? (
                        <span>{lang === 'sq' ? 'Duke dërguar...' : 'Sending...'}</span>
                      ) : (
                        <>
                          {t.contact.send}
                          <Send className="ml-3 w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};